import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import numeral from 'numeral';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';

// Sem carga nova há mais dias que isso, o cartão de atualização fica amarelo
const DIAS_PARA_ALERTA = 120;

const formatHa = (valor) => numeral(valor).format('0,0');

// Mesma janela de v_risco_prod: o ano corrente só entra a partir de setembro
export function janelaDeRisco(totais) {
  let ultimo = Math.max(...totais.map(({ ano }) => ano));
  if (ultimo === moment().year() && moment().month() < 8) ultimo -= 1;
  return [ultimo - 2, ultimo];
}

function Indicadores({ totais, risco, status }) {
  const anoCorrente = moment().year();

  const fechados = totais ? totais.filter(({ ano }) => ano < anoCorrente) : [];
  const ultimo = fechados[fechados.length - 1];
  const anterior = fechados[fechados.length - 2];
  const variacao = ultimo && anterior && anterior.ha ? (ultimo.ha - anterior.ha) / anterior.ha : null;

  const serie = fechados.slice(-10);
  const maiorSerie = Math.max(1, ...serie.map(({ ha }) => ha));
  const pontos = serie
    .map(({ ha }, i) => `${(2 + (i * 196) / Math.max(1, serie.length - 1)).toFixed(1)},${(48 - (ha / maiorSerie) * 44).toFixed(1)}`)
    .join(' ');

  const [inicioJanela, fimJanela] = totais && totais.length ? janelaDeRisco(totais) : [null, null];
  const janela = totais ? totais.filter(({ ano }) => ano >= inicioJanela && ano <= fimJanela) : [];
  const totalJanela = janela.reduce((soma, { ha }) => soma + ha, 0);
  const maiorJanela = Math.max(1, ...janela.map(({ ha }) => ha));

  const alto = risco ? risco.alto || 0 : 0;
  const baixo = risco ? risco.baixo || 0 : 0;
  const familias = alto + baixo;
  const percentualAlto = familias ? Math.round((alto / familias) * 100) : 0;

  const atualizacao = status && status.last_db_update ? moment(parseFloat(status.last_db_update)) : null;
  const diasSemCarga = atualizacao ? moment().diff(atualizacao, 'days') : null;
  const atrasado = diasSemCarga !== null && diasSemCarga > DIAS_PARA_ALERTA;

  return (
    <Faixa aria-label="Indicadores principais">
      <Cartao>
        <Rotulo>Desmatamento em {ultimo ? ultimo.ano : '...'}</Rotulo>
        <Valor>
          {ultimo ? formatHa(ultimo.ha) : '...'} <Unidade>ha</Unidade>
        </Valor>
        {variacao !== null && (
          <Variacao className={variacao <= 0 ? 'queda' : 'alta'}>
            <FontAwesomeIcon icon={variacao <= 0 ? faArrowDown : faArrowUp} />
            {Math.abs(Math.round(variacao * 100))}% {variacao <= 0 ? 'menos' : 'mais'} que {anterior.ano}
            <span>({formatHa(anterior.ha)} ha)</span>
          </Variacao>
        )}
        {serie.length > 1 && (
          <>
            <svg width="100%" height="52" viewBox="0 0 200 52" preserveAspectRatio="none" role="img"
              aria-label={`Desmatamento anual de ${serie[0].ano} a ${serie[serie.length - 1].ano}`}>
              <polyline points={pontos} fill="none" stroke="var(--vft-accent)" strokeWidth="2"
                strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
            <Extremos>
              <span>{serie[0].ano}</span>
              <span>{serie[serie.length - 1].ano}</span>
            </Extremos>
          </>
        )}
      </Cartao>

      <Cartao>
        <Rotulo>Últimos 3 anos {inicioJanela ? `(${inicioJanela}–${fimJanela})` : ''}</Rotulo>
        <Valor>
          {totais ? formatHa(totalJanela) : '...'} <Unidade>ha</Unidade>
        </Valor>
        <Detalhe>Janela usada no cálculo de risco de cada família</Detalhe>
        <MiniBarras>
          {janela.map(({ ano, ha }) => (
            <div key={ano}>
              <div
                className={ano === anoCorrente ? 'barra parcial' : 'barra'}
                style={{ height: `${Math.max(2, Math.round((ha / maiorJanela) * 44))}px` }}
                title={`${ano}: ${formatHa(ha)} ha`}
              />
              <span>{ano === anoCorrente ? `${ano}*` : ano}</span>
            </div>
          ))}
        </MiniBarras>
      </Cartao>

      <Cartao>
        <Rotulo>Famílias em risco alto</Rotulo>
        <Valor>
          {risco ? numeral(alto).format('0,0') : '...'} <Unidade>de {numeral(familias).format('0,0')}</Unidade>
        </Valor>
        <Detalhe>
          <strong className="destaque">{percentualAlto}%</strong> das famílias fornecedoras
        </Detalhe>
        <BarraRisco aria-hidden="true">
          <div style={{ width: `${percentualAlto}%` }} />
        </BarraRisco>
        <Extremos>
          <span>Alto: {numeral(alto).format('0,0')}</span>
          <span>Baixo: {numeral(baixo).format('0,0')}</span>
        </Extremos>
      </Cartao>

      <Cartao className={atrasado ? 'atrasado' : ''}>
        <CabecalhoStatus>
          <Rotulo>Dados atualizados</Rotulo>
          {atualizacao && (
            <Selo className={atrasado ? 'atrasado' : 'em-dia'}>
              <span />
              {atrasado ? 'Desatualizado' : 'Em dia'}
            </Selo>
          )}
        </CabecalhoStatus>
        <Valor>{atualizacao ? atualizacao.format('DD/MM/YYYY') : '...'}</Valor>
        {atrasado ? (
          <>
            <Detalhe>Sem carga nova há {diasSemCarga} dias</Detalhe>
            <Detalhe>Rode o ETL para buscar PRODES e DETER</Detalhe>
          </>
        ) : (
          <>
            {status && status.last_deter && (
              <Detalhe>Último alerta DETER: {moment(status.last_deter).format('DD/MM/YYYY')}</Detalhe>
            )}
            <Detalhe>Fica amarelo após {DIAS_PARA_ALERTA} dias sem carga</Detalhe>
          </>
        )}
      </Cartao>
    </Faixa>
  );
}

const Faixa = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 12px;
`;

const Cartao = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  background: var(--vft-surface);
  border: 1px solid var(--vft-border);
  border-radius: 12px;

  &.atrasado {
    background: #fff9ec;
    border-color: var(--vft-warning-border);
  }
`;

const Rotulo = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--vft-muted);
`;

const Valor = styled.div`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--vft-ink);
`;

const Unidade = styled.span`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0;
  color: var(--vft-muted);
`;

const Detalhe = styled.div`
  font-size: 13px;
  color: var(--vft-muted);

  .destaque {
    color: var(--vft-accent);
  }
`;

const Variacao = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;

  &.queda {
    color: var(--vft-success);
  }
  &.alta {
    color: var(--vft-accent);
  }
  span {
    font-weight: 400;
    color: var(--vft-muted);
  }
`;

const Extremos = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11.5px;
  color: var(--vft-muted);
`;

const MiniBarras = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 64px;
  margin-top: 4px;

  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .barra {
    background: var(--vft-accent);
    border-radius: 4px 4px 0 0;
  }
  .barra.parcial {
    background: var(--vft-accent-soft);
    border: 1.5px dashed var(--vft-accent);
    border-bottom: 0;
  }
  span {
    font-size: 11.5px;
    color: var(--vft-muted);
    text-align: center;
  }
`;

const BarraRisco = styled.div`
  height: 12px;
  margin-top: 12px;
  border-radius: 6px;
  overflow: hidden;
  background: #dde5dd;

  div {
    height: 100%;
    background: var(--vft-accent);
  }
`;

const CabecalhoStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
  &.em-dia {
    background: var(--vft-success-soft);
    color: var(--vft-success);
  }
  &.atrasado {
    background: var(--vft-warning-soft);
    color: var(--vft-warning);
  }
`;

export default Indicadores;
